import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useReactToPrint} from "react-to-print";
import {PDFDownloadLink} from '@react-pdf/renderer';
import axios from "axios";
import Button from "@mui/material/Button";
import {authInterceptor} from "api/apiClient";
import {useAuth} from "hooks/useAuth";
import Loading from "shared/ui/Loading";
import Icon from "shared/ui/Icon";
import {APIPostExternalReports, APIPostReport} from "features/reports/testReport/api/report";
import ReportComponent from "features/reports/testReport/components/school/Report";
import ReportDigitalLiteracy from "../components/digitalLiteracy/Report";
import ReportNumericLiteracy from "../components/numericLiteracy/Report";
import ReportLiteracy from "../components/literacy/Report";
import ReportPdf from "../components/school/pdf/Report";
import ReportPdfLiteracy from "../components/literacy/pdf/Report";
import ReportPdfDigitalLiteracy from "../components/digitalLiteracy/pdf/Report";
import ReportPdfNumericLiteracy from "../components/numericLiteracy/pdf/Report";

import {
    getNumericLiteracyCategoryName,
    getNumericLiteracyGradeLevels, getNumericLiteracySubcategories,
    markedLevel,
    markedLevelBorder,
    ReportLeftBottom,
    ReportSegmentsImage,
    scoreGrades, scoreLevelCssClass
} from "../utils/reportsFunctions";
import StandardsReport from "../components/StandardsReport";
import Box from "@mui/material/Box";

const TEST_MODE = {
    LITERACY: 5,
    DIGITAL_LITERACY: 6,
    NUMERIC_LITERACY: 8,
};

const SUBJECTS_EXCLUDED = ["fizica", "chimie", "geografie", "biologie"];

const handleTestType = (testModeId: number) => {
    if (testModeId === TEST_MODE.LITERACY) return "literacy";
    if (testModeId === TEST_MODE.DIGITAL_LITERACY) return "digitalLiteracy";
    if (testModeId === TEST_MODE.NUMERIC_LITERACY) return "numericLiteracy";
    return "school";
};

const handleCoverImage = (reportType: string) => {
    if (reportType === "istorie") return "/images/reports/cover-istorie.png";
    if (
        reportType === "matematica" ||
        reportType === "bac-matematica" ||
        reportType === "evaluare-matematica" ||
        reportType === "fizica"
    ) {
        return "/images/reports/cover-img.png";
    }
    if (reportType === "romana" || reportType === "evaluare-romana" || reportType === "bac-romana") {
        return "/images/reports/cover-romana.png";
    }
    return "/images/reports/cover-istorie.png";
};

export const sanitizeHtmlDescription = (description: string) => {
    if (!description || Array.isArray(description)) return description;
    const withoutStyleAttributes = description.replace(/style="(.*?)"/gm, "");
    return withoutStyleAttributes
        .replace("<i>", "<b>")
        .replace("</i>", "</b>")
        .replace("<em>", "<b>")
        .replace("</em>", "</b>");
};

const Report = () => {
    const reportPrintRef = useRef(null);
    const {id = ""} = useParams();
    const {update} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [report, setReport] = useState<any>([]);
    const [generatePdf, setGeneratePdf] = useState(false);
    const [isStandardsReport, setIsStandardsReport] = useState<boolean>(false);

    const [searchParams] = useSearchParams();
    const payload = searchParams.get("payload");

    useEffect(() => {
        if (id !== "") {
            setLoading(true);

            if (payload !== null) {
                APIPostExternalReports({payload: payload})
                    .then((response) => {
                        if (response.data.success) {
                            if(response.data.data) {
                                setReport(response.data.data);
                                setIsStandardsReport(response.data.data.learning_standards);
                            } else setReport([]);
                        }
                        else update("error", {show: true, code: "A002"});
                    })
                    .catch(() => update("error", {show: true, code: "A001"}))
                    .finally(() => {
                        axios.interceptors.request.eject(authInterceptor);
                        setLoading(false);
                    });
            } else {
                let locationArray = location.pathname.split("/");
                const archivedLocation = locationArray[locationArray.length - 3];
                APIPostReport({test_id: id, isArchived: archivedLocation === "istoric-teste"})
                    .then((response) => {
                        if (response.data.success) {
                            if(response.data.data) {
                                setReport(response.data.data);
                                setIsStandardsReport(response.data.data.learning_standards);
                            } else setReport([]);
                        } else update("error", {show: true, code: "A002"});
                    })
                    .catch(() => update("error", {show: true, code: "A001"}))
                    .finally(() => setLoading(false));
            }

        } else {
            navigate("/teste");
        }
    }, [id]);

    const mapContent = () => {

        const testType = handleTestType(report.test_mode.id);

        const reportSummary = {
            score: report.score,
            type: report.type,
            testId: report.test.id,
            testTitle: report.test.title,
            testModeId: report.test_mode.id,
            testGradeName: (report.test_mode.id === 8 || report.test_mode.id === 6 )? report.class : report.test.grade.name,
            testStartedAt: report.test.started_at,
            subject: report.subject,
            studentName: report.student_name,
            coverImage: handleCoverImage(report.type),
            subjectsExcluded: [...SUBJECTS_EXCLUDED],
            graphLeft: ReportLeftBottom(report.score, testType)[0],
            graphBottom: ReportLeftBottom(report.score, testType)[1],
            graphSegmentsImage: ReportSegmentsImage(report.score, testType),
        };

        const gradeLevels = report.test_mode.id === 8 ?
            getNumericLiteracyGradeLevels(report.score, report.class)
            :
            report.grade_levels.map((gradeLevel: any) => ({
            gradeLevel: gradeLevel.level,
            description: sanitizeHtmlDescription(gradeLevel.description),
            border: markedLevelBorder(report.score, gradeLevel.level, testType)[0],
            color: markedLevelBorder(report.score, gradeLevel.level, testType)[1],
            scoreTitle: scoreGrades(gradeLevel.level, testType),
            isMarked: markedLevel(report.score, gradeLevel.level, testType),
            cssClass: scoreLevelCssClass(gradeLevel.level, testType),
        }));

        const numericLiteracyGraphData = report.graph_data.map((graphData: any, index: number) => ({
            ...graphData,
            score: report.score,
            name:getNumericLiteracyCategoryName(index),
            subcategoriesDescription: getNumericLiteracySubcategories(report.class)
        }));

        const graphData = report.test_mode.id === 8 ? numericLiteracyGraphData :  report.graph_data;

        const skillsData = report.skills_data?.map((skill: any) => ({
            score: skill.score,
            description: sanitizeHtmlDescription(skill.description),
            numbering1: skill.numbering1,
            numbering2: skill.numbering2,
            categoryName: skill.subcategory.category.name,
            subcategoryName: skill.subcategory.name,
            scoreDescription: skill.score_description,
            gradeLevels: skill.score_description.length > 0 ? skill.score_description.map((description: any) => ({
                gradeLevel: description.level,
                description: sanitizeHtmlDescription(description.description),
                border: markedLevelBorder(report.skills_data.score, description.level, testType)[0],
                color: markedLevelBorder(skill.score, description.level, testType)[1],
                scoreTitle: scoreGrades(description.level, testType),
                isMarked: markedLevel(skill.score, description.level, testType),
                cssClass: scoreLevelCssClass(description.level, testType),
            })) : [],
            graphLeft: ReportLeftBottom(skill.score, testType)[0],
            graphBottom: ReportLeftBottom(skill.score, testType)[1],
            graphSegmentsImage: ReportSegmentsImage(skill.score, testType),
            color: markedLevelBorder(skill.score, skill.level, testType)[1],
        }));

        return { reportSummary, gradeLevels, graphData, skillsData };
    }

    const handlePrint = useReactToPrint({
        content: () => reportPrintRef.current,
    });

    if(loading) {
        return (
            <Loading show={true}/>
        )
    }

    if(report.length === 0) {
        return (
            <Box component="main">
                Sesiunea nu există sau raportul nu este disponibil.
            </Box>
        )
    }

    return (
        <>
            {isStandardsReport ? (
                <>
                    <div ref={reportPrintRef} className="print">
                        <Box component="main">
                            <StandardsReport report={report} isPrinting={true}/>
                        </Box>
                    </div>
                    <Box component="main">
                        <StandardsReport report={report} isPrinting={false}/>
                    </Box>
                </>
            ) : (
                <div ref={reportPrintRef}>
                    {report.test_mode.id === 5 && (
                        <ReportLiteracy
                            reportSummary = {mapContent().reportSummary}
                            gradeLevels = {mapContent().gradeLevels}
                            graphData = {mapContent().graphData}
                        />
                    )}
                    {report.test_mode.id === 6 && (
                        <ReportDigitalLiteracy
                            reportSummary = {mapContent().reportSummary}
                            gradeLevels = {mapContent().gradeLevels}
                            graphData = {mapContent().graphData}
                            skillsData = {mapContent().skillsData}
                        />
                    )}

                    {report.test_mode.id === 8 && (
                        <ReportNumericLiteracy
                            reportSummary = {mapContent().reportSummary}
                            gradeLevels = {mapContent().gradeLevels}
                            graphData = {mapContent().graphData}
                            skillsData = {mapContent().skillsData}
                        />
                    )}

                    {report.test_mode.id < 5 && (
                        <ReportComponent
                            reportSummary = {mapContent().reportSummary}
                            gradeLevels = {mapContent().gradeLevels}
                            graphData = {mapContent().graphData}
                            skillsData = {mapContent().skillsData}
                        />
                    )}
                </div>
            )}

            <div className="print-bar screen no-print">
                <Button type="submit" variant="contained" onClick={handlePrint} style={{marginRight: 20}}>
                    Tipărește
                </Button>
                {!generatePdf ? (
                    !isStandardsReport ? (
                        <Button variant="contained" onClick={() => setGeneratePdf(true)}>
                            Generează pdf
                        </Button>
                        ) : <></>
                ) : (
                    <>
                        {report.test_mode.id === 5 && (
                            <PDFDownloadLink document={
                                <ReportPdfLiteracy
                                    reportSummary = {mapContent().reportSummary}
                                    gradeLevels = {mapContent().gradeLevels}
                                    graphData = {mapContent().graphData}
                                />
                            } fileName="RaportEvaluare.pdf">
                                {({loading}) =>
                                    loading ?
                                        <>Se generează
                                            <Icon
                                                icon="far circle-notch" spin={true}
                                                className="fa-faster" fixedWidth={true}
                                                sx={{display: "inline"}}
                                            /></>
                                        : "Descarcă pdf"
                                }
                            </PDFDownloadLink>
                        )}
                        {report.test_mode.id === 6 && (
                            <PDFDownloadLink
                                document={
                                    <ReportPdfDigitalLiteracy
                                        reportSummary = {mapContent().reportSummary}
                                        gradeLevels = {mapContent().gradeLevels}
                                        graphData = {mapContent().graphData}
                                        skillsData = {mapContent().skillsData}
                                    />}
                                fileName="RaportEvaluare.pdf"
                            >
                                {({loading}) =>
                                    loading ?
                                        <>Se generează
                                            <Icon icon="far circle-notch" spin={true}
                                                  className="fa-faster" fixedWidth={true}
                                                  sx={{display: "inline"}}
                                            /></>
                                        : "Descarcă pdf"
                                }
                            </PDFDownloadLink>
                        )}
                        {report.test_mode.id === 8 && (
                            <PDFDownloadLink
                                document={
                                    <ReportPdfNumericLiteracy
                                        reportSummary = {mapContent().reportSummary}
                                        gradeLevels = {mapContent().gradeLevels}
                                        graphData = {mapContent().graphData}
                                        skillsData = {mapContent().skillsData}
                                    />}
                                fileName="RaportEvaluare.pdf"
                            >
                                {({loading}) =>
                                    loading ?
                                        <>Se generează
                                            <Icon
                                                icon="far circle-notch" spin={true}
                                                className="fa-faster" fixedWidth={true}
                                                sx={{display: "inline"}}
                                            /></>
                                        : "Descarcă pdf"
                                }
                            </PDFDownloadLink>
                        )}
                        {report.test_mode.id < 5 && (
                            <>
                                <PDFDownloadLink
                                    document={
                                        <ReportPdf
                                            reportSummary = {mapContent().reportSummary}
                                            gradeLevels = {mapContent().gradeLevels}
                                            graphData = {mapContent().graphData}
                                            skillsData = {mapContent().skillsData}
                                        />}
                                    fileName="RaportEvaluare.pdf"
                                >
                                    {({loading}) => {
                                        return loading ?
                                            <>Se generează
                                                <Icon
                                                    icon="far circle-notch" spin={true}
                                                    className="fa-faster" fixedWidth={true}
                                                    sx={{display: "inline"}}
                                                /></>
                                            : "Descarcă pdf"
                                    }}
                                </PDFDownloadLink>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    )
};

export default Report;
